// Service layer for posts - UPDATED TO MATCH BACKEND ENDPOINTS AND DB STRUCTURE
const baseURL = '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('zhat_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const postService = {
  healthCheck: async () => {
    try {
      const response = await fetch(`${baseURL}/health`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        return { success: true, data };
      } else {
        const errorText = await response.text();
        return { success: false, error: errorText };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get posts (feed) - Filters out comments by default
  getPosts: async (limit = 20, offset = 0, show_comments = false) => {
    try {
      const response = await fetch(
        `${baseURL}/posts?limit=${limit}&offset=${offset}&show_comments=${show_comments}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch posts: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.posts : [];
    } catch (error) {
      console.error('Failed to get posts:', error);
      throw error;
    }
  },

  // Create post or comment (reply_to_post_id for comments)
  createPost: async (content, fileId = null, reply_to_post_id = null) => {
    try {
      const response = await fetch(`${baseURL}/posts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          content, 
          file_id: fileId,
          reply_to_post_id: reply_to_post_id
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create post');
      }
      
      const result = await response.json();
      return result.success ? result.post : null;
    } catch (error) {
      console.error('Failed to create post:', error);
      throw error;
    }
  },

  // Get comments for a post
  getComments: async (postId, limit = 20, offset = 0) => {
    try {
      const response = await fetch(
        `${baseURL}/posts/${postId}/comments?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch comments: ${response.status}`);
      }
      
      const data = await response.json();
      return data.success ? data.comments : [];
    } catch (error) {
      console.error('Failed to get comments:', error);
      throw error;
    }
  },

  // Like post (toggle)
  likePost: async (postId) => {
    try {
      const response = await fetch(`${baseURL}/posts/${postId}/like`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to like post');
      }
      
      const result = await response.json();
      return result.success ? { 
        liked: result.liked, 
        likes_count: result.likes_count 
      } : null;
    } catch (error) {
      console.error('Failed to like post:', error);
      throw error;
    }
  },

  // Repost (toggle) – returns full backend response
  repost: async (postId) => {
    try {
      const response = await fetch(`${baseURL}/posts/${postId}/repost`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to repost');
      }

      const result = await response.json();
      return result.success ? result : null; // return the whole result object
    } catch (error) {
      console.error('Failed to repost:', error);
      throw error;
    }
  },

  // Delete repost (explicit)
  deleteRepost: async (postId) => {
    try {
      const response = await fetch(`${baseURL}/posts/${postId}/repost`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete repost');
      }
      
      const result = await response.json();
      return result.success ? { 
        reposted: result.reposted, 
        reposts_count: result.reposts_count 
      } : null;
    } catch (error) {
      console.error('Failed to delete repost:', error);
      throw error;
    }
  },

  // Delete post
  deletePost: async (postId) => {
    try {
      const response = await fetch(`${baseURL}/posts/${postId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete post');
      }
      
      const result = await response.json();
      return result.success ? { 
        was_comment: result.was_comment 
      } : null;
    } catch (error) {
      console.error('Failed to delete post:', error);
      throw error;
    }
  },

  // Get single post
  getPost: async (postId) => {
    try {
      const response = await fetch(`${baseURL}/posts/${postId}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get post');
      }
      
      const result = await response.json();
      return result.success ? result.post : null;
    } catch (error) {
      console.error('Failed to get post:', error);
      throw error;
    }
  },

  // Get user's posts by address
  getUserPosts: async (address, limit = 20, offset = 0) => {
    try {
      const response = await fetch(
        `${baseURL}/posts/user/${address}?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to get user posts');
      }
      
      const data = await response.json();
      return data.success ? data.posts : [];
    } catch (error) {
      console.error('Failed to get user posts:', error);
      throw error;
    }
  },

  // Get user's posts by ENS name
  getPostsByENS: async (ensName, limit = 20, offset = 0) => {
    try {
      const response = await fetch(
        `${baseURL}/posts/ens/${ensName}?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to get ENS posts');
      }
      
      const data = await response.json();
      return data.success ? data.posts : [];
    } catch (error) {
      console.error('Failed to get ENS posts:', error);
      throw error;
    }
  },

  // Get user profile by ENS
  getUserProfile: async (ensName) => {
    try {
      const response = await fetch(`${baseURL}/profile/${ensName}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }
      
      const result = await response.json();
      return result.success ? result.profile : null;
    } catch (error) {
      console.error('Failed to get user profile:', error);
      throw error;
    }
  },

  // Get timeline (posts from users you follow)
  getTimeline: async (limit = 20, offset = 0) => {
    try {
      const response = await fetch(
        `${baseURL}/posts/timeline?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to get timeline');
      }
      
      const data = await response.json();
      return data.success ? data.posts : [];
    } catch (error) {
      console.error('Failed to get timeline:', error);
      throw error;
    }
  },

  // Get trending posts
  getTrending: async (limit = 10) => {
    try {
      const response = await fetch(`${baseURL}/posts/trending?limit=${limit}`, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to get trending posts');
      }
      
      const data = await response.json();
      return data.success ? data.posts : [];
    } catch (error) {
      console.error('Failed to get trending posts:', error);
      throw error;
    }
  },

  // File upload
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`/api/files/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('zhat_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      const result = await response.json();
      console.log('📦 uploadFile raw response:', JSON.stringify(result, null, 2));
      return result;
    } catch (error) {
      console.error('Failed to upload file:', error);
      throw error;
    }
  },

  // ===== FOLLOW SYSTEM =====

  // Check if current user follows another user
  checkFollowStatus: async (address) => {
    try {
      const response = await fetch(`${baseURL}/users/${address}/follow-status`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to check follow status');
      const data = await response.json();
      return data.following;
    } catch (error) {
      console.error('Error checking follow status:', error);
      return false;
    }
  },

  // Follow a user
  followUser: async (address) => {
    try {
      const response = await fetch(`${baseURL}/users/${address}/follow`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to follow user');
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error following user:', error);
      throw error;
    }
  },

  // Unfollow a user
  unfollowUser: async (address) => {
    try {
      const response = await fetch(`${baseURL}/users/${address}/follow`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to unfollow user');
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error unfollowing user:', error);
      throw error;
    }
  },

  // Get followers
  getFollowers: async (address) => {
    try {
      const response = await fetch(`${baseURL}/users/${address}/followers`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to get followers');
      const data = await response.json();
      return data.followers;
    } catch (error) {
      console.error('Error fetching followers:', error);
      return [];
    }
  },

  // Get following
  getFollowing: async (address) => {
    try {
      const response = await fetch(`${baseURL}/users/${address}/following`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to get following');
      const data = await response.json();
      return data.following;
    } catch (error) {
      console.error('Error fetching following:', error);
      return [];
    }
  }
};
